import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Block, BlockConfig } from "../../sim/cms";
import { listProducts, startCheckout, type Product } from "../../sim/payments";
import { ProductCard } from "./ProductCard";
import { usePetalsBuy } from "./PetalsBuy";

/**
 * Renders CMS content blocks (banner / text / hero / cards / spacer) from their config. Used by
 * public site pages: a page loads its blocks and maps them through <BlockView>. Missing/partial
 * config falls back to sensible defaults so a half-filled block never crashes the page.
 */

/** A link that routes internally for site paths ("/…") and opens externally otherwise. */
function SmartLink({ to, className, children }: { to: string; className?: string; children: React.ReactNode }) {
  if (/^https?:\/\//i.test(to)) return <a className={className} href={to} target="_blank" rel="noopener noreferrer">{children}</a>;
  return <Link className={className} to={to || "/"}>{children}</Link>;
}

function wrapStyle(c: BlockConfig): React.CSSProperties {
  return {
    maxWidth: c.maxWidth ? `${c.maxWidth}px` : undefined,
    textAlign: c.align,
    background: c.bg || undefined,
    marginLeft: c.align === "center" ? "auto" : undefined,
    marginRight: c.align === "center" ? "auto" : undefined,
  };
}

export function BlockView({ block }: { block: Block }) {
  const c = block.config || {};
  switch (block.type) {
    case "banner": {
      const inner = (
        <img
          className="cms-banner-img"
          src={c.imageUrl || ""}
          alt={c.alt || ""}
          style={{
            width: c.width ? `${c.width}px` : "100%",
            height: c.height ? `${c.height}px` : undefined,
            borderRadius: c.radius != null ? `${c.radius}px` : undefined,
            objectFit: "cover",
          }}
        />
      );
      if (!c.imageUrl) return null;
      return (
        <div className="cms-block cms-banner" style={{ textAlign: c.align || "center" }}>
          {c.link ? <SmartLink to={c.link} className="cms-banner-link">{inner}</SmartLink> : inner}
        </div>
      );
    }
    case "text":
      return (
        <div className="cms-block cms-text" style={wrapStyle(c)}>
          {c.heading && <h2 className="cms-text-heading">{c.heading}</h2>}
          {c.body && <div className="cms-text-body">{renderParagraphs(c.body)}</div>}
        </div>
      );
    case "hero":
      return (
        <div className="cms-block cms-hero" style={{ textAlign: c.align || "center", background: c.bg || undefined }}>
          {c.eyebrow && <div className="cms-hero-eyebrow">{c.eyebrow}</div>}
          {c.heading && <h1 className="cms-hero-title">{c.heading}</h1>}
          {c.body && <p className="cms-hero-sub">{c.body}</p>}
          {c.buttonText && (
            <div className="cms-hero-cta">
              <SmartLink to={c.buttonLink || "/play"} className="site-btn primary">{c.buttonText}</SmartLink>
            </div>
          )}
        </div>
      );
    case "cards":
      return (
        <div className="cms-block cms-cards site-cards">
          {(c.cards || []).map((card, i) => {
            const body = (
              <>
                {card.icon && <div className="site-card-ic">{card.icon}</div>}
                {card.title && <div className="site-card-title">{card.title}</div>}
                {card.sub && <div className="site-card-sub">{card.sub}</div>}
              </>
            );
            return card.link
              ? <SmartLink key={i} to={card.link} className="site-card">{body}</SmartLink>
              : <div key={i} className="site-card">{body}</div>;
          })}
        </div>
      );
    case "spacer":
      return <div className="cms-block cms-spacer" style={{ height: `${c.height ?? 24}px` }} />;
    case "embed":
      return <EmbedBlock c={c} />;
    case "products":
      return <ProductsBlock c={c} />;
    default:
      return null;
  }
}

/**
 * CUSTOM CODE block — the admin's HTML/CSS/JS runs inside a SANDBOXED <iframe> (srcdoc). The sandbox has
 * `allow-scripts` but NOT `allow-same-origin`, so the frame gets a UNIQUE (null) origin: it can run its own
 * JS but cannot read the parent DOM, cookies, localStorage, or the Clerk session — a compromised/mistyped
 * block can't XSS the site. The frame posts its content height back so we can size it to fit (no inner scroll).
 */
function EmbedBlock({ c }: { c: BlockConfig }) {
  const ref = useRef<HTMLIFrameElement | null>(null);
  const [height, setHeight] = useState<number>(c.height ?? 120);
  const nonce = useRef(Math.random().toString(36).slice(2)).current;

  const srcdoc = `<!doctype html><html><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>html,body{margin:0;padding:0;background:transparent;color:inherit;font-family:system-ui,sans-serif}
${c.css ?? ""}</style></head>
<body>${c.html ?? ""}
<script>(function(){try{${c.js ?? ""}}catch(e){console.error(e);}
function report(){try{var h=Math.ceil(document.documentElement.getBoundingClientRect().height);parent.postMessage({__cmsEmbed:${JSON.stringify(nonce)},height:h},"*");}catch(e){}}
report();window.addEventListener("load",report);new ResizeObserver(report).observe(document.documentElement);setInterval(report,1000);})();<\/script>
</body></html>`;

  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      const d = e.data;
      if (d && d.__cmsEmbed === nonce && typeof d.height === "number" && d.height > 0) {
        setHeight(Math.min(4000, Math.max(20, d.height)));
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [nonce]);

  if (!c.html && !c.css && !c.js) return null;
  return (
    <div className="cms-block cms-embed" style={{ maxWidth: c.maxWidth ? `${c.maxWidth}px` : undefined, marginLeft: "auto", marginRight: "auto", padding: c.padY ? `${c.padY}px 0` : undefined }}>
      <iframe
        ref={ref}
        className="cms-embed-frame"
        // SECURITY: allow-scripts only (no allow-same-origin) → null-origin sandbox, isolated from the site.
        sandbox="allow-scripts allow-popups allow-forms"
        srcDoc={srcdoc}
        style={{ width: "100%", height, border: 0, display: "block" }}
        title="custom content"
        loading="lazy"
      />
    </div>
  );
}

/** PRODUCT-HIGHLIGHT block — pulls live products (public GET /api/products) and shows the selected ones (or
 *  all active) as a grid, with a Buy control (sign-in gated in auth builds) exactly like the Shop page. */
function ProductsBlock({ c }: { c: BlockConfig }) {
  const [products, setProducts] = useState<Product[] | null>(null);
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const [paused, setPaused] = useState(false);
  const [busy, setBusy] = useState<number | null>(null);
  const [petalsErr, setPetalsErr] = useState<string | null>(null);
  const petals = usePetalsBuy(setPetalsErr);

  useEffect(() => {
    let alive = true;
    listProducts().then((r) => { if (alive) { setProducts(r.products); setPaymentsEnabled(r.paymentsEnabled); setPaused(r.purchasesPaused); } })
      .catch(() => { if (alive) setProducts([]); });
    return () => { alive = false; };
  }, []);

  if (products === null) return <div className="cms-block cms-products"><p className="site-page-sub">Loading…</p></div>;
  const ids = c.productIds && c.productIds.length ? new Set(c.productIds) : null;
  const shown = ids ? products.filter((p) => ids.has(p.id)) : products;
  if (shown.length === 0) return null;
  const showPrice = c.showPrice !== false;
  const cols = Math.max(1, Math.min(6, c.columns ?? 3));

  async function buy(p: Product) {
    setBusy(p.id);
    try { const url = await startCheckout(p.id); window.location.assign(url); }
    catch { setBusy(null); }
  }

  return (
    <div className="cms-block cms-products" style={{ maxWidth: c.maxWidth ? `${c.maxWidth}px` : undefined, marginLeft: "auto", marginRight: "auto", padding: c.padY ? `${c.padY}px 0` : undefined }}>
      {c.heading && <h2 className="cms-text-heading" style={{ textAlign: c.align || "center" }}>{c.heading}</h2>}
      {petalsErr && <div className="admin-error">⚠ {petalsErr}</div>}
      <div className="site-shop-grid" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
        {shown.map((p) => (
          <ProductCard key={p.id} product={p} onBuy={buy} busy={busy === p.id} enabled={paymentsEnabled} showPrice={showPrice} petals={petals} paused={paused} />
        ))}
      </div>
    </div>
  );
}

/** Split a text body on blank lines into paragraphs (keeps it simple + safe — no raw HTML). */
function renderParagraphs(body: string) {
  return body.split(/\n{2,}/).map((para, i) => <p key={i}>{para}</p>);
}

/** Render a whole list of blocks. */
export function BlockList({ blocks }: { blocks: Block[] }) {
  return <>{blocks.map((b) => <BlockView key={b.id} block={b} />)}</>;
}
